import { computed, ref } from 'vue';
import { useStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type {
  SavedReport,
  SavedReportAvailabilityChecks,
  SavedReportChanges,
  SavedReportSnapshot,
  SavedReportTemplate,
} from '../interfaces';
import type { FiltersProps } from '../types';
import { SYSTEM_MODALS } from '@Atoms';
import { useSessionStore } from '@Stores';
import { ROLES } from '@AuthModule';
import { useDataTableFilters } from '@DataTableModule';
import { ListSavedReportsService } from '../services';
import useActiveFilters from './useActiveFilters';
import useFilters from './useFilters';

const { activeFilters } = useActiveFilters();

// NOTE :: Module-scoped like the other filter state: the list view applies the default report before its
// first fetch, and OSavedReports reads the same selection. `baselineSnapshot` is taken right after a report
// is applied, so a filter or column the report named but the list no longer has does not count as a change.
const activeSavedReport = ref<SavedReport | null>(null);
const baselineSnapshot = ref<SavedReportSnapshot | null>(null);
// NOTE :: The reader's own reports, per list (shared reports live on the Shared Reports page, not here). Each list
// loads its own when it opens (fetchSavedReports), so saved reports never ride on the profile.
const savedReportsByList = ref<Record<string, SavedReport[]>>({});
// NOTE :: The columns a person had before opening a report on a list, kept in the browser and keyed by user +
// list. The list itself also saves its layout in the browser, so leaving the page with a report open (or opening
// it with a starred default) would store the report's columns as the person's own; this entry is set only when
// empty and cleared only when the report is closed, so closing always gives their own layout back.
const columnsBeforeReport = useStorage<Record<string, string[]>>('af-saved-report-columns', {});

// NOTE :: Set by the mounted filters dialog: it knows which filters and columns the list may show (a column the
// shelter admin switched off is gone, with its filter). A report never restores either.
// Only the dialog of the list that has saved reports registers (the cart's product list, which can open on top of
// that list, has its own dialog and must not replace or clear these), and it clears only what it registered.
let availabilityChecks: SavedReportAvailabilityChecks | null = null;

export default function () {
  const sessionStore = useSessionStore();
  const { currentUserId, isShelterAdmin, shelterProfile } = storeToRefs(sessionStore);

  const { clearFilters } = useFilters();
  const { activeColumns, defaultVisibleColumns, initialColumns, page, selectFilter, selectFilterDefaults } =
    useDataTableFilters();

  // NOTE :: A report manager shares their own reports with the whole shelter and manages the Shared Reports page.
  // The api makes the same check (SavedReportController::isReportManager); `can` already lets super admins in.
  const isReportManager = computed(() => isShelterAdmin.value || sessionStore.can(ROLES.MANAGE_SHARED_REPORTS));
  const isSavedReportsEnabled = computed(() => !!shelterProfile.value?.enable_saved_reports);

  // HELPERS
  function hasModelValue(model: FiltersProps['model']) {
    if (Array.isArray(model)) return !!model.length;

    return !!model;
  }

  // NOTE :: Only a LEADING "&" is dropped: live selectFilter strings start with one, stored report strings don't,
  // and a stored "location=A&status=x" must keep the "&" between its params.
  function getSelectFilterParams(value?: string) {
    return new URLSearchParams(value?.replace(/^&/, '') || '');
  }

  // NOTE :: Only the params that differ from their dropdown's default are kept, sorted, so the same view
  // always gives the same string and "Location: All" never counts as a saved choice.
  function getPickedSelectFilter() {
    const params = getSelectFilterParams(selectFilter.value);
    const picked = [...params.entries()].filter(
      ([label, value]) => value && value !== selectFilterDefaults.value[label]
    );

    return new URLSearchParams(picked.sort(([first], [second]) => first.localeCompare(second))).toString();
  }

  function getDefaultSelectFilter() {
    const params = new URLSearchParams(selectFilterDefaults.value);

    return params.toString() ? `&${params.toString()}` : '';
  }

  function getColumnsBeforeReportKey(dataTableId: string) {
    return `${currentUserId.value ?? 'anonymous'}:${dataTableId}`;
  }

  function rememberColumnsBeforeReport(dataTableId: string) {
    const key = getColumnsBeforeReportKey(dataTableId);
    if (columnsBeforeReport.value[key]) return;

    columnsBeforeReport.value = { ...columnsBeforeReport.value, [key]: [...activeColumns.value] };
  }

  // NOTE :: Called when a list opens with no starred report: a layout left over from a report that was still open
  // when the person left is dropped, so the columns they have now count as their own. With a starred report the
  // entry is kept, or closing it could never give their own layout back.
  function forgetColumnsBeforeReport(dataTableId: string) {
    takeColumnsBeforeReport(dataTableId);
  }

  function takeColumnsBeforeReport(dataTableId?: string) {
    if (!dataTableId) return undefined;

    const key = getColumnsBeforeReportKey(dataTableId);
    const { [key]: columns, ...otherLists } = columnsBeforeReport.value;

    columnsBeforeReport.value = otherLists;

    return columns;
  }

  function getSnapshot(): SavedReportSnapshot {
    const filters = activeFilters.value
      .filter(({ model }) => hasModelValue(model))
      .sort((first, second) => first.name.localeCompare(second.name))
      .reduce<SavedReportSnapshot['filters']>((result, { name, model }) => ({ ...result, [name]: model }), {});

    return {
      columns: [...activeColumns.value].sort(),
      filters,
      select_filter: getPickedSelectFilter(),
    };
  }

  function isStandardView() {
    const { columns, filters, select_filter } = getSnapshot();
    const defaultColumns = [...defaultVisibleColumns.value].sort();
    const hasDefaultColumns = JSON.stringify(columns) === JSON.stringify(defaultColumns);

    return !Object.keys(filters).length && !select_filter && hasDefaultColumns;
  }

  function getChanges(): SavedReportChanges | null {
    if (!activeSavedReport.value || !baselineSnapshot.value) return null;

    const current = getSnapshot();
    const baseline = baselineSnapshot.value;
    const filterNames = new Set([...Object.keys(current.filters), ...Object.keys(baseline.filters)]);

    return {
      columns: JSON.stringify(current.columns) !== JSON.stringify(baseline.columns),
      filters: [...filterNames].filter(
        (name) => JSON.stringify(current.filters[name]) !== JSON.stringify(baseline.filters[name])
      ),
      selectFilter: current.select_filter !== baseline.select_filter,
    };
  }

  function hasChanges() {
    const changes = getChanges();
    if (!changes) return false;

    return changes.columns || changes.selectFilter || !!changes.filters.length;
  }

  function getDefaultSavedReport(dataTableId: string) {
    if (!isSavedReportsEnabled.value) return undefined;

    return getListSavedReports(dataTableId).find(({ is_default }) => is_default);
  }

  // NOTE :: The app owns the order (case- and accent-blind, "émile" before "zoo"), so a list reads the same after a
  // save and after a reload; the api's own `name_key` sort compares bytes and would put "émile" after "zoo".
  function getListSavedReports(dataTableId: string) {
    return [...(savedReportsByList.value[dataTableId] ?? [])].sort((first, second) =>
      first.name.localeCompare(second.name, undefined, { sensitivity: 'base' })
    );
  }

  // NOTE :: OSavedReports hosts its own dialogs, and ADialog is one shared modal. A list view that renders its
  // own <ADialog v-if="activeModal"> must skip it while one of these is open, or two dialogs show at once.
  // NOTE :: Reads the SYSTEM_MODALS enum directly: calling useADialog() here would register a new watcher on every
  // check (this runs inside computeds), and those watchers are never removed.
  function isSavedReportModal(modal: string) {
    const SAVED_REPORT_MODALS: string[] = [
      SYSTEM_MODALS.SAVED_REPORT_DELETE,
      SYSTEM_MODALS.SAVED_REPORT_SAVE,
      SYSTEM_MODALS.SAVED_REPORT_UPDATE,
    ];

    return SAVED_REPORT_MODALS.includes(modal);
  }

  // NOTE :: The api refuses every write with this code once the shelter turns the feature off. The page keeps
  // showing the control until the next load, so the caller says why instead of "something went wrong".
  function isSavedReportsOffError(error: any) {
    return error?.response?.data?.code === 'saved_reports_off';
  }

  // NOTE :: The api answers 404 when the report is gone since this page loaded: deleted in another tab or device, or,
  // for a shared report on the Shared Reports page, no longer shared. The caller drops the dead row instead of
  // offering a retry that always fails.
  function isSavedReportGoneError(error: any) {
    return error?.response?.status === 404;
  }

  // EVENTS
  // NOTE :: Returns true when part of the report no longer exists on the list (a renamed filter or a removed
  // column). That part is skipped instead of failing, and the caller tells the user.
  function applySavedReport(report: SavedReport) {
    const knownFilterNames = activeFilters.value
      .map(({ name }) => name)
      .filter((name) => availabilityChecks?.isFilterAvailable(name) ?? true);
    const hasMissingFilters = Object.keys(report.filters).some((name) => !knownFilterNames.includes(name));
    const knownColumns = report.columns.filter(
      (column) => initialColumns.value.includes(column) && (availabilityChecks?.isColumnAvailable(column) ?? true)
    );
    const hasMissingColumns = knownColumns.length !== report.columns.length;

    clearFilters();

    activeFilters.value = activeFilters.value.map((filter) =>
      filter.name in report.filters && knownFilterNames.includes(filter.name)
        ? { ...filter, model: report.filters[filter.name] }
        : filter
    );

    const pickedParams = getSelectFilterParams(report.select_filter);
    const selectParams = new URLSearchParams(selectFilterDefaults.value);
    pickedParams.forEach((value, label) => selectParams.set(label, value));
    selectFilter.value = selectParams.toString() ? `&${selectParams.toString()}` : selectFilter.value;

    rememberColumnsBeforeReport(report.data_table_id);

    activeColumns.value = knownColumns.length ? knownColumns : defaultVisibleColumns.value;
    page.value = 1;

    activeSavedReport.value = report;
    baselineSnapshot.value = getSnapshot();

    return hasMissingFilters || hasMissingColumns;
  }

  function clearSavedReport() {
    clearFilters();

    selectFilter.value = getDefaultSelectFilter() || selectFilter.value;
    const columnsBefore = (takeColumnsBeforeReport(activeSavedReport.value?.data_table_id) ?? []).filter((column) =>
      initialColumns.value.includes(column)
    );

    activeColumns.value = columnsBefore.length ? columnsBefore : defaultVisibleColumns.value;
    page.value = 1;

    activeSavedReport.value = null;
    baselineSnapshot.value = null;
  }

  // NOTE :: After a save or an update the list already shows exactly what was stored, so the current view
  // becomes the new baseline — no re-apply, no refetch.
  function selectSavedReport(report: SavedReport) {
    rememberColumnsBeforeReport(report.data_table_id);

    activeSavedReport.value = report;
    baselineSnapshot.value = getSnapshot();
  }

  // NOTE :: A list that fails to load its reports still opens, just without them: the list itself matters more
  // than the saved views on top of it, and the request's own error already reached the app's error handler.
  function fetchSavedReports(dataTableId: string) {
    if (!isSavedReportsEnabled.value) return Promise.resolve(setSavedReports(dataTableId, []));

    return ListSavedReportsService(dataTableId)
      .then(({ data }) => setSavedReports(dataTableId, data))
      .catch(() => setSavedReports(dataTableId, []));
  }

  function setSavedReports(dataTableId: string, reports: SavedReport[]) {
    savedReportsByList.value = { ...savedReportsByList.value, [dataTableId]: reports };
  }

  // NOTE :: Granular store update from the api response. The star is the reader's own and the api keeps one per
  // list, so a new default unstars the reader's other reports on the same list here too.
  function upsertSavedReport(savedReport: SavedReport) {
    const otherReports = (savedReportsByList.value[savedReport.data_table_id] ?? [])
      .filter(({ _id }) => _id !== savedReport._id)
      .map((report) => (savedReport.is_default ? { ...report, is_default: false } : report));

    setSavedReports(savedReport.data_table_id, [...otherReports, savedReport]);

    if (activeSavedReport.value?._id === savedReport._id) activeSavedReport.value = savedReport;
  }

  function removeSavedReport(savedReportId: string) {
    savedReportsByList.value = Object.entries(savedReportsByList.value).reduce<Record<string, SavedReport[]>>(
      (lists, [dataTableId, reports]) => ({
        ...lists,
        [dataTableId]: reports.filter(({ _id }) => _id !== savedReportId),
      }),
      {}
    );
  }

  function registerAvailabilityChecks(checks: SavedReportAvailabilityChecks) {
    availabilityChecks = checks;
  }

  function unregisterAvailabilityChecks(checks: SavedReportAvailabilityChecks) {
    if (availabilityChecks !== checks) return;

    availabilityChecks = null;
  }

  // NOTE :: "View filter" opens a template as a report the reader does not own: its view is applied and the
  // toolbar offers to save it as their own report (a copy) or close it, never to update it.
  function getTemplatePreview(template: SavedReportTemplate): SavedReport {
    return {
      _id: template._id,
      columns: template.columns,
      data_table_id: template.data_table_id,
      filters: template.filters,
      is_default: false,
      is_owner: false,
      shareable: true,
      name: template.name,
      owner_name: template.owner_name,
      select_filter: template.select_filter,
      source_template_id: null,
    };
  }

  function reset() {
    activeSavedReport.value = null;
    baselineSnapshot.value = null;
    savedReportsByList.value = {};
  }

  return {
    activeSavedReport,
    isReportManager,
    isSavedReportsEnabled,

    applySavedReport,
    clearSavedReport,
    fetchSavedReports,
    forgetColumnsBeforeReport,
    getChanges,
    getDefaultSavedReport,
    getListSavedReports,
    getSnapshot,
    getTemplatePreview,
    hasChanges,
    isSavedReportGoneError,
    isSavedReportModal,
    isSavedReportsOffError,
    isStandardView,
    removeSavedReport,
    reset,
    selectSavedReport,
    registerAvailabilityChecks,
    unregisterAvailabilityChecks,
    upsertSavedReport,
  };
}
